import React, { useEffect, useState } from "react";
import { Card, Paper, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { DashboardApi } from "../../api/Dashboard/DasboardApi";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { numberLocaleFromI18n } from "../../utils/i18nLocale";
import { pageChartCardSx, pageChartTitleSx } from "../../styles/pageModernStyles";
import { DASHBOARD_CHART_COLORS } from "../Dashboard/dashboardChartUtils";
import DashboardChartTooltip from "../Dashboard/DashboardChartTooltip";
import { dashboardFixedTooltipProps } from "../../utils/dashboardChartUtils";

import "dayjs/locale/es";
import "dayjs/locale/en";
import "dayjs/locale/zh-cn";

const DesignMesAcumulado = ({ year }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [data, setData] = useState([]);
  const [designers, setDesigners] = useState([]);

  const { i18n, t } = useTranslation();

  useEffect(() => {
    let lang = i18n.language.toLowerCase();
    if (lang === "ch" || lang.startsWith("zh")) lang = "zh-cn";
    else if (lang.startsWith("en")) lang = "en";
    else if (lang.startsWith("es")) lang = "es";
    else lang = "en";

    dayjs.locale(lang);
  }, [i18n.language]);

  useEffect(() => {
    const fetchDesigns = async () => {
      try {
        const designs = await DashboardApi.getDashboardDesigns(year);
        const uniqueDesigners = [...new Set(designs.map((r) => r.Designer))];
        setDesigners(uniqueDesigners);

        const months = Array.from({ length: 12 }, (_, i) => i + 1);
        const acumulados = {};
        const monthlyData = months.map((m) => {
          const row = {
            month: dayjs()
              .month(m - 1)
              .format("MMM"),
          };

          uniqueDesigners.forEach((designer) => {
            const countThisMonth = designs.filter(
              (d) => d.Month === m && d.Designer === designer,
            ).length;

            acumulados[designer] = (acumulados[designer] || 0) + countThisMonth;
            row[designer] = acumulados[designer];
          });

          return row;
        });

        setData(monthlyData);
      } catch (error) {
        console.error("❌ Error cargando diseños acumulados:", error);
      }
    };

    fetchDesigns();
  }, [year, i18n.language]);

  const colors = DASHBOARD_CHART_COLORS;
  const locale = numberLocaleFromI18n(i18n.language);

  return (
    <Card component={Paper} sx={pageChartCardSx(theme, { tall: true })}>
      <Typography variant="h6" sx={pageChartTitleSx}>
        {t("DashboardDesign.AccumulatedDesignsDesigner")} ({year})
      </Typography>

      <ResponsiveContainer width="100%" height="85%">
        <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
          <XAxis
            dataKey="month"
            tick={{ fontSize: isMobile ? 10 : 12, fill: theme.palette.text.primary }}
          />
          <YAxis
            width={isMobile ? 40 : 70}
            tick={{ fontSize: isMobile ? 10 : 12, fill: theme.palette.text.primary }}
            tickFormatter={(v) => v.toLocaleString(locale)}
          />
          <Tooltip
            {...dashboardFixedTooltipProps}
            content={(props) => (
              <DashboardChartTooltip
                {...props}
                valueFormatter={(value) =>
                  Number(value).toLocaleString(locale, {
                    maximumFractionDigits: 0,
                  })
                }
              />
            )}
          />
          <Legend
            verticalAlign="bottom"
            align="center"
            wrapperStyle={{
              fontSize: "11px",
              marginTop: "-10px",
              paddingBottom: 0,
            }}
          />

          {designers.map((designer, idx) => (
            <Line
              key={designer}
              type="monotone"
              dataKey={designer}
              stroke={colors[idx % colors.length]}
              strokeWidth={isMobile ? 1.5 : 2}
              dot={{ r: isMobile ? 2 : 3 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};

export default DesignMesAcumulado;
